import { io, Socket } from 'socket.io-client';

// ── Socket.io client singleton ──────────────────────────────────────────

let socket: Socket | null = null;

const SOCKET_URL = import.meta.env.VITE_API_URL || '';
const SOCKET_PATH = '/ws';

export function getSocket(): Socket {
  if (!socket) {
    socket = io(`${SOCKET_URL}${SOCKET_PATH}`, {
      // Read on every connection attempt, not once. The stored token is renewed while the page stays
      // open, and the gateway drops a handshake with an expired token for good (the client does not
      // retry a server-side disconnect) — so a reconnect after an API restart must present the current one.
      auth: (cb) => cb({ token: localStorage.getItem('isp_token') }),
      transports: ['websocket', 'polling'],
      autoConnect: false,
      reconnection: true,
      // Never stop trying. This used to give up after 10 attempts, and giving up is permanent — the
      // client sits there showing "Offline" until someone reloads the page, even once the API is
      // back. An API restart is exactly when a live dashboard must heal itself unattended, so it
      // retries indefinitely with the delay capped at 30s; an idle reconnect attempt every half
      // minute costs nothing next to a dashboard that has silently stopped updating.
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 30000,
      timeout: 20000,
    });
  }
  return socket;
}

export function connectSocket(): void {
  const s = getSocket();
  const token = localStorage.getItem('isp_token');

  if (token && !s.connected) {
    s.connect();
  }
}

export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

export function reconnectSocket(): void {
  disconnectSocket();
  connectSocket();
}

// ── Event type definitions ──────────────────────────────────────────────

export interface WsNotification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  link?: string;
}

export interface WsDeviceStatus {
  deviceId: string;
  deviceType: 'nas' | 'olt' | 'onu';
  name: string;
  isReachable: boolean;
  /** false ⇒ reachable via management TCP but SNMP agent not answering ("Online – SNMP Unresponsive"). */
  snmpResponsive?: boolean;
  metrics?: Record<string, unknown>;
}

export interface WsTicketUpdate {
  ticketId: string;
  ticketNumber: string;
  action: 'created' | 'updated' | 'assigned' | 'resolved' | 'closed';
  summary: string;
}

export interface WsSubscriberChange {
  subscriberId: string;
  subscriberCode: string;
  action: 'created' | 'updated' | 'activated' | 'suspended' | 'expired';
  name: string;
}

export interface WsBillingEvent {
  type: 'invoice_generated' | 'payment_received' | 'payment_failed';
  subscriberId?: string;
  amount?: number;
  count?: number;
}

export interface WsJobProgress {
  jobId: string;
  jobName: string;
  progress: number;
  status: 'running' | 'completed' | 'failed';
  message?: string;
}

export interface WsDashboardUpdate {
  metric: string;
  value: number;
  previousValue?: number;
}

// ── OLT/ONU real-time event types ─────────────────────────────────────

export interface WsOnuStatusChange {
  oltId: string;
  oltName: string;
  ponPort: number;
  onuIndex: number;
  serialNumber?: string;
  previousStatus: string;
  newStatus: string;
  source: 'polling' | 'trap';
}

export interface WsOnuAlarm {
  oltId: string;
  oltName: string;
  ponPort: number;
  onuIndex: number;
  serialNumber?: string;
  alarmType: 'low_rx_power' | 'high_tx_power' | 'onu_offline' | 'los';
  severity: 'warning' | 'critical';
  rxPower?: number;
  txPower?: number;
  threshold?: number;
}

export interface WsOnuDiscovered {
  oltId: string;
  oltName: string;
  ponPort: number;
  onuIndex: number;
  serialNumber: string;
  status: string;
  macAddress?: string;
}

export interface WsDeviceRecovery {
  deviceId: string;
  deviceType: 'olt';
  name: string;
  isReachable: true;
  downtimeSeconds: number | null;
  previousFailures: number;
  recoveredAt: string;
}

export interface WsOnuSignalUpdate {
  onuId: string;
  serialNumber: string;
  rxPower: number | null;
  txPower: number | null;
  oltRxPower: number | null;
  status: string;
  lastPolledAt: string;
}

// ── Field dispatch ──
export interface WsTechnicianLocation {
  userId: string;
  name?: string;
  lat: number;
  lng: number;
  status?: string;
  at: string;
  battery?: number;
}

export interface WsTechnicianStatus {
  userId: string;
  status: string;
}

export interface WsJobUpdate {
  jobId: string;
  jobNumber: string;
  status: string;
  assignedTo?: string | null;
  action: 'created' | 'assigned' | 'accepted' | 'rejected' | 'status' | 'reassigned' | 'completed' | 'photo';
}

export interface WsMisrouteAlert {
  jobId: string;
  jobNumber: string;
  technicianId: string;
  technicianName?: string;
  reasons: string[];
  severity: string;
  plannedKm: number | null;
  actualKm: number | null;
  extraKm: number | null;
  plannedEtaMin: number | null;
  projectedEtaMin: number | null;
  extraMin: number | null;
  navUrl: string;
  detectedAt: string;
}
